import { RecipeRealization } from '../api/Types';
import { SupabaseService } from './SupabaseClient';
import { storageService } from './StorageService';
import { serviceMappers } from './serviceMappers';
import { REALIZATION_WITH_AUTHOR_SELECT } from './serviceSelectors';
import { normalizeAuthorRelation, trimToNull } from './serviceHelpers';
import { postToNetlifyFunction, readNetlifyErrorMessage } from './netlifyClient';

interface RawRecipeRealization {
  id: number;
  recipe_id: string;
  author_id: number;
  comment: string | null;
  image: string | null;
  created_at: string;
  updated_at: string;
  author:
    | {
        id: number;
        name: string;
        first_name: string;
        avatar_url?: string | null;
        created_at?: string;
        color?: string;
      }
    | {
        id: number;
        name: string;
        first_name: string;
        avatar_url?: string | null;
        created_at?: string;
        color?: string;
      }[]
    | null;
}

interface RecipeRealizationPayload {
  recipe_id: string;
  author_id: number;
  comment: string | null;
  image: string | null;
}

export class RecipeRealizationService extends SupabaseService {
  private throwIfQueryFailed(error: { message?: string } | null): void {
    if (error) {
      throw new Error(error.message);
    }
  }

  private normalizeRealizations(realizations: RawRecipeRealization[]): RecipeRealization[] {
    return serviceMappers.normalizeRecipeRealizations(realizations.map((realization) => ({
      id: realization.id,
      recipe_id: realization.recipe_id,
      author_id: realization.author_id,
      comment: realization.comment,
      image: realization.image,
      created_at: realization.created_at,
      updated_at: realization.updated_at,
      author: normalizeAuthorRelation(realization.author),
    })));
  }

  private normalizeRealization(realization: RawRecipeRealization): RecipeRealization {
    return this.normalizeRealizations([realization])[0];
  }

  private normalizeRealizationPayload(payload: RecipeRealizationPayload): RecipeRealizationPayload {
    return {
      ...payload,
      comment: trimToNull(payload.comment),
      image: trimToNull(payload.image),
    };
  }

  private assertRealizationContent(payload: RecipeRealizationPayload): void {
    if (!payload.comment && !payload.image) {
      throw new Error('Ajoute une photo, un commentaire, ou les deux.');
    }
  }

  private async upsertRealizationRow(payload: RecipeRealizationPayload): Promise<RawRecipeRealization> {
    const supabaseClient = await this.getClient();
    const { data, error } = await supabaseClient
      .from('recipe_realizations')
      .upsert([payload], {
        onConflict: 'recipe_id,author_id',
      })
      .select(REALIZATION_WITH_AUTHOR_SELECT)
      .single();

    this.throwIfQueryFailed(error);
    if (!data) {
      throw new Error('Recipe realization upsert returned no data.');
    }

    return (data as unknown) as RawRecipeRealization;
  }

  private async requestRealizationImageDeletion(
    realizationId: number,
    filePath: string
  ): Promise<Response | null> {
    return postToNetlifyFunction({
      functionName: 'delete-recipe-realization-image',
      tokenHeaderName: 'x-client-token',
      missingTokenStrategy: 'skip',
      payload: {
        realizationId,
        filePath,
      },
    });
  }

  public async listRecipeRealizations(recipeId: string): Promise<RecipeRealization[]> {
    const supabaseClient = await this.getClient();
    const { data, error } = await supabaseClient
      .from('recipe_realizations')
      .select(REALIZATION_WITH_AUTHOR_SELECT)
      .eq('recipe_id', recipeId)
      .order('created_at', { ascending: false });

    this.throwIfQueryFailed(error);

    return this.normalizeRealizations(((data || []) as unknown) as RawRecipeRealization[]);
  }

  public async upsertMyRecipeRealization(payload: RecipeRealizationPayload): Promise<RecipeRealization> {
    const normalizedPayload = this.normalizeRealizationPayload(payload);
    this.assertRealizationContent(normalizedPayload);

    const data = await this.upsertRealizationRow(normalizedPayload);
    return this.normalizeRealization(data);
  }

  public async deleteMyRecipeRealization(realizationId: number): Promise<void> {
    const supabaseClient = await this.getClient();
    const { error } = await supabaseClient
      .from('recipe_realizations')
      .delete()
      .eq('id', realizationId);

    this.throwIfQueryFailed(error);
  }

  public async uploadRealizationImage(file: File, authorId: number): Promise<string> {
    return storageService.uploadImage(file, `public/realizations/author-${authorId}`);
  }

  public async deleteRealizationImage(realizationId: number, imageUrl: string): Promise<void> {
    const filePath = storageService.extractFilePathFromImageUrl(imageUrl);

    if (!filePath) {
      return;
    }

    const response = await this.requestRealizationImageDeletion(realizationId, filePath);

    if (!response) {
      return;
    }

    if (!response.ok) {
      const detail = await readNetlifyErrorMessage(
        response,
        'Impossible de supprimer l\'image de la realisation.'
      );
      throw new Error(detail || 'Impossible de supprimer l\'image de la realisation.');
    }
  }
}

export const recipeRealizationService = new RecipeRealizationService();
